import { LDate } from '@app/components/date/LDate';
import { LTime } from '@app/components/date/LTime';
import { InfoM } from '@app/modules/audit/modules/aod/screens/aod-rule-view/components/InfoM';
import { auditReports } from '@mi-front/api-clients';
import { Box, Grid, Typography } from '@mui/material';
import { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import { GipReportStationsInfo } from './GipReportStationsInfo';

export function GipReportInfo({
  config,
  showDateRange = true,
}: {
  config: auditReports.GipReportDto['data']['config'];
  showDateRange?: boolean;
}) {
  const [customFieldKey] = Object.keys(config.customFields!);
  const customFieldValue = customFieldKey ? config.customFields![customFieldKey] : null;

  return (
    <Box data-cy="list-filter">
      {showDateRange && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <InfoM
              label={
                <FormattedMessage
                  id="audit.gipReport.info.start"
                  defaultMessage="Fecha/hora de inicio"
                />
              }
            >
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <LDate value={config.from} day="2-digit" month="2-digit" year="2-digit" />
                <LTime value={config.from} />
              </Box>
            </InfoM>
          </Grid>
          <Grid item xs={12} md={3}>
            <InfoM
              label={
                <FormattedMessage
                  id="audit.gipReport.info.end"
                  defaultMessage="Fecha/hora de fin"
                />
              }
            >
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <LDate value={config.to} day="2-digit" month="2-digit" year="2-digit" />
                <LTime value={config.to} />
              </Box>
            </InfoM>
          </Grid>
        </Grid>
      )}

      <Box sx={{ mb: 3 }}>
        <GipReportStationsInfo config={config as unknown as auditReports.GipReportDataDto} />
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <InfoM
            label={
              <FormattedMessage
                id="audit.gipReport.form.advertisers"
                defaultMessage="Anunciantes"
              />
            }
          >
            <Typography variant="body2">
              {config.advertisers!.length > 0 &&
                config.advertisers!.map((value, index) => (
                  <Fragment key={index}>
                    {index > 0 && (
                      <Typography
                        component="span"
                        key={value}
                        sx={{ color: 'text.secondary', mx: 0.5 }}
                      >
                        {' '}
                        /{' '}
                      </Typography>
                    )}
                    {value}
                  </Fragment>
                ))}
              {!config.advertisers!.length && (
                <Typography component="span" sx={{ color: 'text.secondary' }}>
                  <FormattedMessage
                    id="audit.gipReport.form.allAdvertisers"
                    defaultMessage="Todos los anunciantes"
                  />
                </Typography>
              )}
            </Typography>
          </InfoM>
        </Grid>
        <Grid item xs={12} md={3}>
          <InfoM
            label={
              <FormattedMessage id="audit.gipReport.form.campaigns" defaultMessage="Campañas" />
            }
          >
            <Typography variant="body2">
              {config.campaigns!.length > 0 &&
                config.campaigns!.map((value, index) => (
                  <Fragment key={index}>
                    {index > 0 && (
                      <Typography
                        component="span"
                        key={value}
                        sx={{ color: 'text.secondary', mx: 0.5 }}
                      >
                        {' '}
                        /{' '}
                      </Typography>
                    )}
                    {value}
                  </Fragment>
                ))}
              {!config.campaigns!.length && (
                <Typography component="span" sx={{ color: 'text.secondary' }}>
                  <FormattedMessage
                    id="audit.gipReport.form.allCampaigns"
                    defaultMessage="Todas las campañas"
                  />
                </Typography>
              )}
            </Typography>
          </InfoM>
        </Grid>
        <Grid item xs={12} md={3}>
          <InfoM
            label={
              <FormattedMessage id="audit.gipReport.form.versions" defaultMessage="Versiones" />
            }
          >
            <Typography variant="body2">
              {config.versions!.length > 0 &&
                config.versions!.map((value, index) => (
                  <Fragment key={index}>
                    {index > 0 && (
                      <Typography
                        component="span"
                        key={value}
                        sx={{ color: 'text.secondary', mx: 0.5 }}
                      >
                        {' '}
                        /{' '}
                      </Typography>
                    )}
                    {value}
                  </Fragment>
                ))}
              {!config.versions!.length && (
                <Typography component="span" sx={{ color: 'text.secondary' }}>
                  <FormattedMessage
                    id="audit.gipReport.form.allVersions"
                    defaultMessage="Todas las versiones"
                  />
                </Typography>
              )}
            </Typography>
          </InfoM>
        </Grid>
        <Grid item xs={12} md={3}>
          <InfoM
            label={<FormattedMessage id="audit.gipReport.form.orders" defaultMessage="Órdenes" />}
          >
            <Typography variant="body2">
              {config.orders!.length > 0 &&
                config.orders!.map((value, index) => (
                  <Fragment key={index}>
                    {index > 0 && (
                      <Typography
                        component="span"
                        key={value}
                        sx={{ color: 'text.secondary', mx: 0.5 }}
                      >
                        {' '}
                        /{' '}
                      </Typography>
                    )}
                    {value}
                  </Fragment>
                ))}
              {!config.orders!.length && (
                <Typography component="span" sx={{ color: 'text.secondary' }}>
                  <FormattedMessage
                    id="audit.gipReport.form.allOrders"
                    defaultMessage="Todas las órdenes"
                  />
                </Typography>
              )}
            </Typography>
          </InfoM>
        </Grid>
      </Grid>

      {customFieldKey && (
        <Grid container spacing={2} sx={{ mt: 3, mb: 2 }}>
          <Grid item xs={12} md={3}>
            <InfoM
              label={
                <FormattedMessage
                  id="audit.gipReport.form.customField"
                  defaultMessage="Campo custom"
                />
              }
            >
              <Typography variant="body2">
                {customFieldKey && (
                  <>
                    {customFieldKey} = "{customFieldValue}"
                  </>
                )}
                {!customFieldKey && (
                  <Typography component="span" sx={{ color: 'text.secondary' }}>
                    <FormattedMessage
                      id="audit.gipReport.noCustomFieldFilter"
                      defaultMessage="Sin filtro de campo personalizado"
                    />
                  </Typography>
                )}
              </Typography>
            </InfoM>
          </Grid>
        </Grid>
      )}

      {!!config.excludedMatches?.length && (
        <Grid container spacing={2} sx={{ mt: 2, mb: 2 }}>
          <Grid item xs={12} md={5}>
            <InfoM
              label={
                <FormattedMessage
                  id="audit.gipReport.excludedMatchesLabel"
                  defaultMessage="Matches excluidos del export"
                />
              }
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <FormattedMessage
                  id="audit.gipReport.excludedMatchesCount"
                  defaultMessage="{count, plural, one {match excluido} other {matches excluidos}}"
                  values={{ count: config.excludedMatches.length }}
                />
              </Typography>
            </InfoM>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
